import { defineComponent, h } from 'vue';
import { ASSIGNEES_ITEMS_DROPDOWN_LIMIT } from '@/_legacy/constants';
import { Icon } from '../icon/Icon';
import { t } from '@/ui/i18n/i18n';
import { CreateUserModal } from '@/ui/components/user/CreateUserModal';
import { canAddUser, canSelfServeSubscription } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { TagField } from '@/ui/components/form';
import { ValidationError } from '@/entities/validation';
import { AddUserSeatsModal } from '@/ui/components/modals/AddUserSeatsModal';
import CannotAddUserSeatsModal from '@/ui/components/modals/CannotAddUserSeatsModal.vue';
import { SelectGroupsModal } from '@/ui/components/role/SelectGroupsModal';
import { useTask } from '@/ui/concerns';
import { useModal } from '@/ui/concerns/modals/useModal';
import { list as listUsers } from '@/services/api/resources/user';
import { UserQuery, defaultUserQuery } from '@/services/api/resources/user/query';

interface ChooseUserFieldWithGroupsProps {
  options: SelectOptions;
  value: number[];
  errors: ValidationError[];
  disabled?: boolean;
  required?: boolean;
  label?: string;
  placeholder?: string;
  identifier?: string;
  hideLabel?: boolean;
  name?: string;
}

export const ChooseUserFieldWithGroups = defineComponent<ChooseUserFieldWithGroupsProps>({
  name: 'ChooseUserFieldWithGroups',
  props: ['options', 'value', 'errors', 'label', 'placeholder', 'identifier', 'hideLabel', 'name'],
  setup(props, ctx) {
    const [isAddUserModalOpen, openAddUserModal, closeAddUserModal] = useModal();
    const [isSelectGroupsModalOpen, openSelectGroupsModal, closeSelectGroupsModal] = useModal();
    const [isAddUserSeatModalOpen, openAddUserSeatModal, closeAddUserSeatModal] = useModal();

    const onCreateUserClick = () => {
      if (canAddUser()) {
        openAddUserModal();
      } else {
        openAddUserSeatModal();
      }
    };

    const onUserCreated = () => {
      closeAddUserModal();
    };

    const overridenDefaultUserQuery = (override: Record<string, unknown>): UserQuery => <UserQuery>({
      ...defaultUserQuery,
      ...override,
    });

    const fetchUsersFromGroupsAndEmit = (groupIds: number[]) => useTask(
      () => listUsers(overridenDefaultUserQuery({ group: groupIds })),
      data => {
        ctx.emit('add', data.data.map(row => row.id));
      },
    );

    const onGroupSelected = (groupIds: number[]) => {
      closeSelectGroupsModal();

      fetchUsersFromGroupsAndEmit(groupIds).run();
    };

    const onUserSeatAdded = () => {
      closeAddUserSeatModal();
      openAddUserModal();
    };

    return function render() {
      return h('div', { class: 'field choose-user-field' }, [
        h(TagField, {
          props: {
            name: props.name ?? 'users',
            value: props.value,
            options: props.options,
            disabled: false,
            placeholder: props.placeholder ?? t('group.enter-users'),
            label: props.label ?? t('common.users'),
            required: false,
            errors: props.errors,
            hideLabel: props.hideLabel,
            inputName: props.name ?? 'users',
            maxListedOptions: ASSIGNEES_ITEMS_DROPDOWN_LIMIT,
          },
          on: {
            input: (value: Array<string | number>) => ctx.emit('input', value),
          },
        }, [
          h('template', {
            slot: 'header',
          }, [
            h('a', { class: 'choose-user-field__header-link', on: { click: openSelectGroupsModal } }, [
              h(Icon, { props: { icon: 'plus', classes: 'has-text-primary' } }),
              h('span', { class: 'has-text-primary has-text-weight-semibold' }, t('role.select-groups')),
            ]),
            h('a', { class: 'choose-user-field__header-link', on: { click: onCreateUserClick } }, [
              h(Icon, { props: { icon: 'plus', classes: 'has-text-primary' } }),
              h('span', { class: 'has-text-primary has-text-weight-semibold' }, t('common.create-user')),
            ]),
            ctx.slots.beforeList ? ctx.slots.beforeList() : null,
          ]),
        ]),
        canSelfServeSubscription.value
          ? h(AddUserSeatsModal, {
            props: {
              active: isAddUserSeatModalOpen.value,
            },
            on: {
              close: closeAddUserSeatModal,
              'created:user-seat': onUserSeatAdded,
            },
          })
          : h(CannotAddUserSeatsModal, {
            props: {
              active: isAddUserSeatModalOpen.value,
            },
            on: {
              close: closeAddUserSeatModal,
            },
          }),
        h(SelectGroupsModal, {
          props: {
            active: isSelectGroupsModalOpen.value,
            identifier: props.identifier,
            width: '500px',
          },
          on: { close: closeSelectGroupsModal, 'groups:selected': onGroupSelected },
        }),
        h(CreateUserModal, {
          props: {
            active: isAddUserModalOpen.value,
            identifier: props.identifier,
          },
          on: { close: closeAddUserModal, 'user:created': onUserCreated },
        }),
      ]);
    };
  },
});

export function chooseUserFieldWithGroups(
  { props, on = {}, classes = '' }: { props: ChooseUserFieldWithGroupsProps; on?: Record<string, Function>; classes?: string },
) {
  return h(ChooseUserFieldWithGroups, { props, on, class: classes });
}
